/**
 * Suite-level severity — maps a suite's success rate (the fraction of passing
 * tests/probes in the suite, 0..1) to a colour band + label. This is the
 * SINGLE SOURCE OF TRUTH for how a whole suite is coloured and named on the
 * page. Tweak the thresholds, hues, or labels here and every suite pill
 * follows. (The per-bucket history bar keeps the finer ok/warn/down palette in
 * `status-mapping.ts` — that answers a different question, "how did each slice
 * do", not "how healthy is the suite overall".)
 *
 * Bands (by % of successful tests in the suite):
 *
 *   ≥ 95%        → operational   green   (no outage)
 *   80% – <95%   → minor outage  amber
 *   50% – <80%   → major outage  orange
 *   < 50%        → service down  red
 *   no data      → no data       grey
 */

export type SuiteSeverity =
  | 'operational'
  | 'minor'
  | 'major'
  | 'down'
  | 'no_data';

/**
 * Lower bounds (INCLUSIVE) per band, as success FRACTIONS (0..1). Edit these
 * to move where a suite flips colour. The upper bound of each band is the
 * next-higher band's lower bound (exclusive), so 95% is operational, 94.99% is
 * a minor outage, 80% is a minor outage, 79.99% is a major outage, etc.
 *
 * NB: values are fractions, not percentages — the API returns success as a
 * 0..1 fraction (`overallSuccessPct`). 0.95 === "95%".
 */
export const SUITE_SEVERITY_THRESHOLDS = {
  /** ≥ 95% of tests pass → operational (green). */
  operational: 0.95,
  /** ≥ 80% pass → minor outage (amber). */
  minor: 0.8,
  /** ≥ 50% pass → major outage (orange). */
  major: 0.5,
  // < 50% pass → service down (red) — the implicit floor band.
} as const;

/**
 * Canonical saturated hue per band — kept in lockstep with the Tailwind tokens
 * in `tailwind.config.js` (`ok` / `warn` / `major` / `down`). Exposed here so
 * the band hues live next to the thresholds for one-stop tuning; the
 * theme-aware surface/border/foreground tints live in `index.css`.
 */
export const SUITE_SEVERITY_HEX: Record<SuiteSeverity, string> = {
  operational: '#1aa66b', // green  — reuses the `ok` token
  minor: '#e0a019', //       amber  — reuses the `warn` token
  major: '#f2872f', //       orange — the `major` token (new)
  down: '#e3503e', //        red    — reuses the `down` token
  no_data: '#8b8b91', //     grey
};

interface SuiteSeverityStyle {
  /** Human label shown in the status pill + aria text. Sentence case. */
  label: string;
  /** Pill container classes (fill + label colour). */
  pill: string;
  /** Pill leading-dot classes. */
  dot: string;
}

/**
 * Per-band presentation. `pill`/`dot` are Tailwind class bundles (same shape
 * as `TONE_BAR` in `status-mapping.ts`) so all the styling hooks for a band
 * live in one place. The pill is a ton.org stat chip: one neutral control
 * fill for every band, the hue carried by the dot. The label stays white
 * while all is well and takes the band's lifted foreground once it isn't,
 * so an outage still reads from across the page (DESIGN.md §5).
 */
const CHIP = 'bg-[rgb(var(--control))]';

export const SUITE_SEVERITY_META: Record<SuiteSeverity, SuiteSeverityStyle> = {
  operational: {
    label: 'Operational',
    pill: `${CHIP} text-ink-1`,
    dot: 'bg-ok',
  },
  minor: {
    label: 'Minor outage',
    pill: `${CHIP} text-warn-fg`,
    dot: 'bg-warn',
  },
  major: {
    label: 'Major outage',
    pill: `${CHIP} text-major-fg`,
    dot: 'bg-major',
  },
  down: {
    label: 'Service down',
    pill: `${CHIP} text-down-fg`,
    dot: 'bg-down',
  },
  no_data: {
    label: 'No data',
    pill: `${CHIP} text-ink-3`,
    dot: 'bg-line-strong',
  },
};

/**
 * Map a success fraction (0..1) to a severity band. `null`/`undefined`/NaN →
 * `no_data`.
 *
 * Note this never returns `no_data` for a real 0%: a suite that ran and failed
 * everything is `down`, not `no_data`. "Ran but all-failed" vs "never ran" is
 * the caller's call — pass `null` only for the latter (e.g. when the backend
 * already reports the component as `no_data`).
 */
export function suiteSeverity(
  successPct: number | null | undefined,
): SuiteSeverity {
  if (
    successPct === null ||
    successPct === undefined ||
    !Number.isFinite(successPct)
  ) {
    return 'no_data';
  }
  if (successPct >= SUITE_SEVERITY_THRESHOLDS.operational) return 'operational';
  if (successPct >= SUITE_SEVERITY_THRESHOLDS.minor) return 'minor';
  if (successPct >= SUITE_SEVERITY_THRESHOLDS.major) return 'major';
  return 'down';
}
